import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Rectangle, Cushion, ScrollBox, Grid, MaxWidth, Typography } from '@pitchero/react-ui';
import { Trans } from '../../../lib/i18n';
import { hasLoadedAssignments } from '../../../store/shop/selectors';
import useAnimation from '../../use-animation';
import ProductCard from '../product-list/product-card';
import AssignmentsCompleteBanner from './complete-banner';

const animationSteps = [
  {
    maxHeight: '0px',
    paddingLeft: '80px',
    opacity: '0.2',
  },
  {
    maxHeight: '350px',
    paddingLeft: '0px',
    opacity: '1',
  },
];

const AssignmentList = ({ products, hasLoaded, canClose }) => {
  // This will store hasLoaded as it was first initalialsed
  // The reason for this is so that we only enable the animation if
  // We have gone from not having the products to having the products
  // If we have had the products from initialisation we just want to render the finished state
  const [enableAnimation] = useState(!hasLoaded && canClose);

  // This is here because hooks relies on call order
  // Attempting to but this under the if statement causes react to throw errors
  const { maxHeight, paddingLeft, opacity } = useAnimation(
    enableAnimation ? animationSteps : animationSteps.slice(1),
    hasLoaded,
  );

  if (!hasLoaded) {
    return null;
  }

  if (!products.length) {
    return <AssignmentsCompleteBanner enableAnimation={enableAnimation} canClose={canClose} />;
  }

  return (
    <Rectangle
      fill="mercury"
      className="shop-assignment-list"
      style={{
        maxHeight,
        opacity,
        overflow: 'hidden',
        height: 'auto',
        willChange: 'max-height, opacity',
        transition: 'max-height 600ms ease, opacity 1000ms ease',
      }}
    >
      <Cushion
        top="medium"
        horizontal="small"
        component="div"
        responsive={[{ minWidth: 'tab', props: { horizontal: 'large' } }]}
      >
        <MaxWidth component="div" maxWidth={1366} center>
          <Typography preset="title">
            <Trans i18nKey="shop:assignment_list_header">You&apos;ve been asked to buy</Trans>
          </Typography>
        </MaxWidth>
      </Cushion>
      <ScrollBox
        xOnly
        style={{ whiteSpace: 'nowrap', paddingLeft, transition: 'padding-left 600ms ease' }}
        component="div"
      >
        <Cushion
          vertical="medium"
          component="div"
          left="small"
          responsive={[
            {
              minWidth: 'tab',
              maxWidth: 1414,
              props: { bottom: 'xlarge', top: 'medium', left: 'large' },
            },
          ]}
        >
          <MaxWidth component="div" maxWidth={1366} center>
            <Grid
              columns={`repeat(${products.length}, 340px)`}
              responsive={[
                {
                  minWidth: 'tab',
                  props: {
                    columns: `repeat(${products.length}, 354px)`,
                  },
                },
              ]}
            >
              {products.map((product) => (
                <Cushion component="div" right="medium" key={product.id}>
                  <ProductCard {...product} forceView="landscape" />
                </Cushion>
              ))}
            </Grid>
          </MaxWidth>
        </Cushion>
      </ScrollBox>
    </Rectangle>
  );
};

AssignmentList.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  canClose: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  hasLoaded: hasLoadedAssignments(state),
});

export default connect(mapStateToProps)(AssignmentList);
